import { useEffect, useRef, useState } from 'react'

import { Box, FloraButton, Flex } from '@grupoboticario/flora-react'
import { TrashIcon } from '@grupoboticario/flora-react-icons'

import RichTextEditor from '@/components/RichTextEditor/RichTextEditor'
import * as S from '@/components/RichTextEditor/RichTextEditor.styles'
import { sanitizeHtmlForMobileApp } from '@/utils/htmlHelpers/htmlHelpers'

import type { AccelerationDetailRichItem } from '../../AccelerationForm.types'
import { plainTextFromHtml } from '../../validation/accelerationFormValidation'
import CharacterCounter from '../counter/CharacterCounter'
import FieldTitle from '../field-title/FieldTitle'

export interface RichTextItemProps {
  item: AccelerationDetailRichItem
  index: number
  label: string
  canDelete: boolean
  maxTextLength: number
  readOnly?: boolean
  onUpdate: (index: number, html: string) => void
  onDelete: (index: number) => void
}

export const RichTextItem = ({
  item,
  index,
  label,
  canDelete,
  maxTextLength,
  readOnly,
  onUpdate,
  onDelete
}: RichTextItemProps) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [charCount, setCharCount] = useState(() => plainTextFromHtml(item.html).length)

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = item.html ? sanitizeHtmlForMobileApp(item.html) : ''
    }
  }, [item.html])

  useEffect(() => {
    setCharCount(plainTextFromHtml(item.html).length)
  }, [item.html])

  const handleBlur = () => {
    if (readOnly) return
    const html = sanitizeHtmlForMobileApp(editorRef.current?.innerHTML ?? '')
    onUpdate(index, html)
  }

  return (
    <Flex align="center" gap="$2" justify="space-between">
      <Box css={{ flex: 1, minWidth: 0 }}>
        <FieldTitle>{`${label} ${index + 1}`}</FieldTitle>
        <RichTextEditor
          editorRef={editorRef}
          formattingCommands={['bold']}
          maxTextLength={maxTextLength}
          minHeight="50px"
          maxHeight="200px"
          isReadOnly={readOnly}
          onBlur={handleBlur}
          onInput={(text) => setCharCount(text.length)}
          placeholder="Escreva aqui..."
          rightButton={
            !readOnly && canDelete ? (
              <FloraButton
                hierarchy="tertiary"
                onClick={() => onDelete(index)}
                type="button"
                css={S.formattingButtonBaseCss}
              >
                <TrashIcon aria-hidden="true" size={18} css={{ color: '$assorted1', marginRight: '$2' }} />
              </FloraButton>
            ) : undefined
          }
        />
        {!readOnly && <CharacterCounter current={charCount} max={maxTextLength} />}
      </Box>
    </Flex>
  )
}
